import { Table } from 'app/shared/components/generic-ui/Table/Table';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import {
  mapDropdowns,
  useBatchHistoryTableConfig,
  useProbeDataFormatter,
} from 'app/shared/hooks';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
import {
  IBatchEvent,
  IHistoryRecord,
  ILatestMeasurement,
  ILatestMeasurementValue,
  IReuseOrderModel,
  Routes,
} from 'types';

import { useStyles } from './BatchList.styles';
import { BatchDetailsModal, ModalType } from 'app/pages/pourEventDetails';
import { format, isValid, parseISO } from 'date-fns';
import { firebaseAnalytics } from 'app/shared/analytics';
import {
  ActionButton,
  ActionButtonStyle,
  IAction,
} from 'app/shared/components/generic-ui/ActionButton/ActionButton';
import { CircularProgress } from 'app/shared/components/generic-ui/CircularProgress';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import {
  MeasurementLinkIcon,
  ReuseModelsItem,
} from 'app/shared/components/generic-ui/ReuseModelsItem/ReuseModelsItem';
import { ReuseModalInfo } from 'app/shared/components/generic-ui/ReuseModelsItem/ReuseModalnfo';
import {
  IReuseModalItemInfoRef,
  ReuseModalItemInfo,
} from 'app/shared/components/generic-ui/ReuseModelsItem/ReuseModalItemInfo';

const oneElement = 1;
const formatMask = 'MMM d, y - hh:mm aaa';

const formatDispatchDate = (value: unknown) => {
  if (
    typeof value !== 'string' ||
    !value.trim() ||
    /^\d+$/.test(value.trim())
  ) {
    return '';
  }
  const date = parseISO(value);
  return isValid(date) ? format(date, formatMask) : '';
};
interface IBatchListProps {
  statuses: Record<string, string[]>;
  batchList?: IHistoryRecord[];
  isLoading?: boolean;
  rowsTotal: number;
  baseURL?: string;
  refreshHandler: () => void;
  setToJobRemaining: Dispatch<SetStateAction<boolean>>;
  setPredictions: Dispatch<SetStateAction<boolean>>;
}

export const BatchList = ({
  statuses,
  batchList,
  isLoading,
  rowsTotal,
  baseURL,
  refreshHandler,
  setToJobRemaining,
  setPredictions,
}: IBatchListProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { organizationId }: { organizationId: string } = useRouteParams();
  const { renderProbeData, formatProbeValue } = useProbeDataFormatter();
  const reuseModalItemInfoRef = useRef<IReuseModalItemInfoRef | null>(null);

  const [crn, setCrn] = useState(
    new URLSearchParams(location.search).get('crn') || '',
  );
  const [isBatchDetailsOpen, setIsBatchDetailsOpen] = useState(!!crn);
  const [modalType, setModalType] = useState<ModalType | undefined>();
  const [info, setInfo] = useState<IReuseOrderModel>();

  const onRefresh = useCallback(() => {
    refreshHandler();
  }, [refreshHandler]);

  const { defaultColumns } = useBatchHistoryTableConfig(onRefresh, statuses);
  const [columns, setColumns] = useState(defaultColumns);

  const onClickRowHandler = (value: IBatchEvent, modalType?: ModalType) => {
    setCrn(value.crn);
    setModalType(modalType);
    setIsBatchDetailsOpen(true);
  };

  useEffect(() => {
    defaultColumns.forEach(({ id, label }) => {
      const propertyId = id.split('property');
      const splittedId = id.split('status');
      if (splittedId.length === 2 && typeof label === 'string') {
        setPredictions(
          prev => prev || label.toLowerCase().includes('predictions'),
        );
      }

      if (propertyId.length === 2 && typeof label === 'string') {
        setToJobRemaining(
          prev => prev || label.toLowerCase().includes('remaining'),
        );
      }
    });
  }, [defaultColumns, setPredictions, setToJobRemaining]);

  const onChangeTableColumns = useCallback(
    (id: string, label: string) => {
      localStorage.setItem(id, label);
      const propertyId = id.split('property');
      const splittedId = id.split('status');
      const columnId = splittedId.length === 2 ? splittedId[1] : 0;
      if (splittedId.length === 2 && typeof label === 'string') {
        setPredictions(
          prev => prev || label.toLowerCase().includes('predictions'),
        );
      }
      if (propertyId.length === 2 && typeof label === 'string') {
        setToJobRemaining(
          prev => prev || label.toLowerCase().includes('remaining'),
        );
      }

      //@ts-ignore
      setColumns(prevState => {
        const currentTableColumns = prevState.map(item => {
          const dropDownValues =
            item.id === `property${columnId}`
              ? mapDropdowns(statuses[label])
              : item.dropDownValues;
          return {
            ...item,
            label:
              item.id === id
                ? label
                : item.id === `property${columnId}`
                ? dropDownValues?.find(value => value.value === item.label)
                    ?.value ||
                  dropDownValues?.[0].value ||
                  '-'
                : item.label,
            dropDownValues: dropDownValues,
          };
        });
        return currentTableColumns;
      });
    },
    [setPredictions, setToJobRemaining, statuses],
  );

  const actions: () => IAction[] = useCallback(() => {
    const allActions = [
      {
        label: t(translations.pourEventPage.addManualData),
        onClick: (row: IBatchEvent) => {
          firebaseAnalytics.logPressEvent(
            Routes.SinglePourEvent,
            'Open Add Manual Data Modal',
          );

          onClickRowHandler(row, ModalType.MANUAL_DATA);
        },
      },
      {
        label: t(translations.pourEventPage.addComprStrength),
        onClick: (row: IBatchEvent) => {
          firebaseAnalytics.logPressEvent(
            Routes.SinglePourEvent,
            'Open Add Compressive Strength Modal',
          );

          onClickRowHandler(row, ModalType.COMPRESSIVE_STRENGTH);
        },
      },
    ];

    return allActions;
  }, [t]);

  const renderCell = useCallback(
    (rowData: any, cellId: string, _index: number, label: string) => {
      if (cellId === 'totalOrdered') {
        return (
          <div>
            {`${formatProbeValue(rowData.cumulativeVolume)}/${renderProbeData(
              rowData[cellId],
            )}`}
          </div>
        );
      } else if (cellId === 'historyRecordName') {
        return (
          <div>
            <CustomTypography
              variant="bodySmall"
              color={rowData[cellId] === 'Voided' ? 'systemRed' : 'accentDark'}
            >
              {rowData[cellId]}
            </CustomTypography>
          </div>
        );
      } else if (cellId === 'dispatchDateTimeUtc') {
        return <div>{formatDispatchDate(rowData.dispatchDateTimeUtc)}</div>;
      } else if (cellId.includes('property')) {
        const propIndx = cellId.charAt(cellId.length - oneElement);
        const currentStatus = columns.find(el => el.id === `status${propIndx}`);

        const latestMeasurement: ILatestMeasurement | undefined =
          rowData?.latestMeasurements?.find(
            (el: ILatestMeasurement) =>
              el.eventStatusCode === currentStatus?.label,
          );

        if (latestMeasurement?.reuseOrderModels && label === 'ReuseOrders') {
          return (
            <ReuseModelsItem
              reuseOrderModels={latestMeasurement.reuseOrderModels}
              setInfo={setInfo}
              refresh={refreshHandler}
              openModal={(type, message, reuseOrderModel) =>
                reuseModalItemInfoRef.current?.open(
                  type,
                  message,
                  reuseOrderModel,
                )
              }
            />
          );
        }

        const currentValue = latestMeasurement?.probeValues?.find(
          (value: ILatestMeasurementValue) => value.propertyName === label,
        )?.propertyValue;

        return (
          <div className={classes.wrapper}>
            <div
              className={
                latestMeasurement?.isManualMeasurement
                  ? classes.valueContainerDark
                  : ''
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {renderProbeData(currentValue)}
                {typeof label === 'string' &&
                  label.replace(/\s/g, '').toLowerCase() === 'delvodosage' && (
                    <MeasurementLinkIcon link={latestMeasurement?.link} />
                  )}
              </div>
            </div>
          </div>
        );
      } else if (cellId === 'actions') {
        return (
          <div>
            <ActionButton
              style={ActionButtonStyle.LIGHT}
              options={actions()}
              rowData={rowData}
            />
          </div>
        );
      } else {
        return (
          <div>
            {typeof rowData[cellId] === 'string'
              ? rowData[cellId]
              : renderProbeData(rowData[cellId])}
          </div>
        );
      }
    },
    [
      actions,
      classes.valueContainerDark,
      classes.wrapper,
      columns,
      formatProbeValue,
      renderProbeData,
      refreshHandler,
    ],
  );

  return (
    <div className={classes.container}>
      {isLoading ? (
        <div className={classes.loading}>
          <CircularProgress withLabel />
        </div>
      ) : null}

      {crn && (
        <BatchDetailsModal
          isOpen={isBatchDetailsOpen}
          crn={crn}
          closeHandler={() => {
            setIsBatchDetailsOpen(false);
            setCrn('');
            onRefresh();
          }}
          modalType={modalType}
        />
      )}
      {info && <ReuseModalInfo info={info} setInfo={setInfo} />}

      <Table
        columns={columns}
        tableData={batchList}
        withCheckbox={false}
        rowsTotal={rowsTotal}
        renderCellWithIndexOfRow={renderCell}
        withColoredRows={true}
        onClickRow={onClickRowHandler}
        withPagination={true}
        baseURL={
          baseURL ||
          Routes.Batches.replace(':organizationId', `${organizationId}`)
        }
        withPerPageCount={true}
        isLoading={false}
        rowId={'crn'}
        emptyTableText={t(translations.pourEventPage.noHistory)}
        headRowsCount={2}
        onChangeHeadCell={onChangeTableColumns}
        withHover={true}
        noHeight
      />
      <ReuseModalItemInfo
        ref={reuseModalItemInfoRef}
        refreshHandler={refreshHandler}
      />
    </div>
  );
};
